<section
  class="flex h-full w-full flex-col overflow-hidden bg-white text-gray-900"
  role="dialog"
  [attr.aria-label]="'board_v3_merge_cards_title' | translate"
>
  <header class="flex shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-3.5">
    <h2 class="m-0 flex-1 text-base font-bold leading-6 text-gray-900">{{ 'board_v3_merge_cards_title' | translate }}</h2>
    <button
      type="button"
      class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
      [attr.aria-label]="'close' | translate"
      (click)="cancel()"
    >
      <app-icon>close</app-icon>
    </button>
  </header>

  <div class="crm-scroll min-h-0 flex-1 overflow-auto bg-gray-50 px-5 py-4">
    @if (isLoading) {
      <div class="flex min-h-64 items-center justify-center">
        <app-loader />
      </div>
    } @else if (!hasEnoughCards) {
      <div class="flex min-h-64 flex-col items-center justify-center gap-2 rounded-xl bg-white px-4 py-8 text-center">
        <app-icon class="text-gray-300">rule</app-icon>
        <p class="m-0 text-sm font-medium text-gray-600">{{ 'board_v3_merge_cards_not_enough' | translate }}</p>
      </div>
    } @else if (loadError) {
      <div class="flex min-h-64 flex-col items-center justify-center gap-2 rounded-xl bg-white px-4 py-8 text-center">
        <app-icon class="text-red-300">error_outline</app-icon>
        <p class="m-0 text-sm font-medium text-gray-600">{{ 'board_v3_merge_cards_load_failed' | translate }}</p>
      </div>
    } @else {
      <div class="grid min-w-max items-stretch" [style.grid-template-columns]="gridTemplateColumns">
        @for (card of cards; track card.id; let cardIndex = $index) {
          <div class="sticky top-0 z-20 bg-gray-50 pb-3">
            <button
              type="button"
              class="flex w-full items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left"
              [ngClass]="
                isPrimary(card.id)
                  ? 'border-[var(--primary-color)] bg-[rgba(var(--primary-color-rgb-code),0.08)]'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              "
              (click)="selectPrimary(card.id)"
            >
              <app-radio
                name="merge-primary-card"
                [value]="card.id"
                [input]="primaryCardId ?? ''"
              />
              <span
                class="min-w-0 flex-1 truncate text-sm font-bold"
                [ngClass]="isPrimary(card.id) ? 'text-[var(--primary-color)]' : 'text-gray-900'"
              >
                {{ cardLabel(cardIndex) }}
              </span>
            </button>
          </div>
          <div class="sticky top-0 z-20 flex items-center justify-center bg-gray-50 pb-3 text-base font-semibold text-gray-300">
            {{ operatorFor(cardIndex) }}
          </div>
        }
        <div class="sticky top-0 z-20 bg-gray-50 pb-3">
          <div class="flex w-full items-center rounded-xl bg-[var(--primary-color)] px-3 py-2.5">
            <span class="min-w-0 flex-1 truncate text-sm font-bold text-white">{{ 'board_v3_merge_cards_result' | translate }}</span>
          </div>
        </div>

        @for (group of groups; track group.key) {
          <div class="col-span-full mb-2 mt-1 flex items-center gap-2">
            <button
              type="button"
              class="flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-sm font-semibold text-gray-700 hover:bg-gray-100"
              (click)="toggleGroup(group)"
            >
              <app-icon class="!text-[18px] !leading-none text-gray-400">
                {{ group.collapsed ? 'chevron_right' : 'expand_more' }}
              </app-icon>
              <span>{{ group.translate ? (group.title | translate) : group.title }}</span>
            </button>
            <span class="h-px flex-1 bg-gray-200"></span>
          </div>

          @if (!group.collapsed) {
            @for (field of group.fields; track field.key) {
              @for (card of cards; track card.id) {
                <div
                  class="flex flex-col gap-1 border-b border-gray-100 px-3 py-2.5"
                  [ngClass]="[
                    isSelected(field, card.id) ? 'bg-[rgba(var(--primary-color-rgb-code),0.08)]' : 'bg-white',
                    field.kind === 'single' ? 'cursor-pointer' : ''
                  ]"
                  (click)="onCellClick(field, card.id)"
                >
                  <span class="text-[11px] leading-4 text-gray-400">
                    {{ field.translate ? (field.title | translate) : field.title }}
                  </span>
                  <span class="flex min-w-0 items-start gap-2">
                    @if (field.kind === 'single') {
                      <app-radio
                        [name]="'merge-' + field.key"
                        [value]="card.id"
                        [input]="selectedSourceFor(field)"
                        [disabled]="valueFor(field, card.id)?.isEmpty ?? true"
                        (onValueChange)="selectSingleValue(field, card.id)"
                      />
                    } @else {
                      <span class="-mt-1 -ml-1 flex shrink-0 items-center">
                        <app-checkbox
                          [checked]="isSelected(field, card.id)"
                          [disabled]="valueFor(field, card.id)?.isEmpty ?? true"
                          [disableRipple]="true"
                          (change)="toggleMultiValue(field, card.id, $event)"
                        />
                      </span>
                    }
                    <span class="min-w-0 flex-1">
                      <ng-container
                        [ngTemplateOutlet]="valueView"
                        [ngTemplateOutletContext]="{ field: field, value: valueFor(field, card.id) }"
                      ></ng-container>
                    </span>
                  </span>
                </div>
                <span class="border-b border-gray-100 bg-gray-50"></span>
              }
              <div class="flex flex-col gap-1 border-b border-gray-100 bg-white px-3 py-2.5">
                <span class="text-[11px] leading-4 text-gray-400">
                  {{ field.translate ? (field.title | translate) : field.title }}
                </span>
                <ng-container
                  [ngTemplateOutlet]="valueView"
                  [ngTemplateOutletContext]="{ field: field, value: resultFor(field) }"
                ></ng-container>
              </div>
            }
          }
        }
      </div>
    }
  </div>

  <footer class="flex shrink-0 items-center gap-3 border-t border-gray-100 px-5 py-3">
    <app-button class="ml-auto" [btnType]="'cancel'" [disabled]="isMerging" (onChangeClick)="cancel()">
      {{ 'cancel' | translate }}
    </app-button>
    <app-button [disabled]="!canMerge" (onChangeClick)="confirm()">
      <span class="flex items-center gap-1">
        @if (isMerging) {
          <app-loader size="tiny" color="pi-white" />
        } @else {
          <app-icon>check</app-icon>
        }
        {{ 'board_v3_merge_cards_confirm' | translate }}
      </span>
    </app-button>
  </footer>
</section>

<ng-template #valueView let-field="field" let-value="value">
  @if (!value || value.isEmpty) {
    <span class="block text-sm text-gray-300">{{ 'board_v3_merge_cards_empty_value' | translate }}</span>
  } @else if (field.render === 'tag' && value.tag) {
    <app-tag [tag]="value.tag" [maxCharacters]="24" />
  } @else if (field.render === 'user' && value.user) {
    <span class="flex min-w-0 items-center gap-2">
      <app-avatar [url]="value.user.avatarUrl" [title]="value.user.name" size="22px" [isPhotoGallery]="false" />
      <span class="min-w-0 flex-1 truncate text-sm text-gray-900" [title]="value.user.name">{{ value.user.name }}</span>
    </span>
  } @else if (field.render === 'list') {
    <span class="flex min-w-0 flex-col gap-0.5">
      @for (line of value.lines; track $index) {
        <span class="truncate text-sm text-gray-900" [title]="line">{{ line }}</span>
      }
    </span>
  } @else {
    <span class="block whitespace-pre-line break-words text-sm text-gray-900" [title]="value.text">{{ value.text }}</span>
  }
</ng-template>
